import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { 
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, 
  DropdownMenuTrigger, DropdownMenuSeparator 
} from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import {
  FileText, Monitor, Users, GraduationCap, CheckSquare,
  BookOpen, Bot, Zap, LogOut, ChevronRight, TrendingUp, Lightbulb, Briefcase, MessageSquare, Coins, HelpCircle, Bell, Settings, Globe, Gift, MapPin, Loader2, Sparkles, Cpu, Shield, Menu, Database, Package, Plane, LifeBuoy, Megaphone, LayoutDashboard, Languages, Building2, Star, Camera, Brain,
} from "lucide-react";
import { useAuth } from "@/App";
import useTranslation from "@/hooks/useTranslation";
import LanguageSelector from "@/components/common/LanguageSelector";
import ThemeSwitcher from "@/components/ThemeSwitcher";
import HubSwitcher from "@/components/HubSwitcher";
import TopNav from "@/components/TopNav";
import AllBaiseLogo from "@/components/AllBaiseLogo";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Country list
const COUNTRIES = [
  { code: "ZA", name: "South Africa" }, { code: "US", name: "United States" },
  { code: "GB", name: "United Kingdom" }, { code: "AU", name: "Australia" },
  { code: "CA", name: "Canada" }, { code: "DE", name: "Germany" },
  { code: "FR", name: "France" }, { code: "IN", name: "India" },
  { code: "JP", name: "Japan" }, { code: "BR", name: "Brazil" },
  { code: "NG", name: "Nigeria" }, { code: "KE", name: "Kenya" },
  { code: "GH", name: "Ghana" }, { code: "EG", name: "Egypt" },
  { code: "AE", name: "UAE" }, { code: "SA", name: "Saudi Arabia" },
  { code: "SG", name: "Singapore" }, { code: "CN", name: "China" },
  { code: "MX", name: "Mexico" }, { code: "IT", name: "Italy" },
  { code: "ES", name: "Spain" }, { code: "NL", name: "Netherlands" },
  { code: "CH", name: "Switzerland" }, { code: "SE", name: "Sweden" },
  { code: "PL", name: "Poland" }, { code: "NZ", name: "New Zealand" },
];

export default function Dashboard() {
  const auth = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    documents: 0,
    members: 0,
    courses: 0,
    tasks: 0,
    drones: 0
  });
  const [recentTasks, setRecentTasks] = useState([]);
  const [credits, setCredits] = useState(0);
  const [unreadCount, setUnreadCount] = useState(0);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [languages, setLanguages] = useState([]);
  const [userLanguage, setUserLanguage] = useState("en");
  const [userCountry, setUserCountry] = useState("");
  const [savingSettings, setSavingSettings] = useState(false);
  const [savingLanguage, setSavingLanguage] = useState(false);
  const [quickPurchaseOpen, setQuickPurchaseOpen] = useState(false);
  const [creditPackages, setCreditPackages] = useState([]);
  const [purchasingPackage, setPurchasingPackage] = useState(null);
  const [llmProvider, setLlmProvider] = useState("");
  const [availableProviders, setAvailableProviders] = useState([]);
  const [providerModels, setProviderModels] = useState({});
  const [savingProvider, setSavingProvider] = useState(false);
  const [totpEnabled, setTotpEnabled] = useState(false);
  const [totpSecret, setTotpSecret] = useState("");
  const [totpProvisioningUri, setTotpProvisioningUri] = useState("");
  const [totpSetupCode, setTotpSetupCode] = useState("");
  const [totpDisablePassword, setTotpDisablePassword] = useState("");
  const [settingUpTotp, setSettingUpTotp] = useState(false);
  const [confirmingTotp, setConfirmingTotp] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Change Password
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [changingPassword, setChangingPassword] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem("token");
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        
        const [statsRes, tasksRes, creditsRes] = await Promise.all([
          axios.get(`${API}/stats`, { headers, withCredentials: true }),
          axios.get(`${API}/tasks`, { headers, withCredentials: true }),
          axios.get(`${API}/credits/balance`, { headers, withCredentials: true }).catch(() => ({ data: { balance: 0 } }))
        ]);
        
        setStats(statsRes.data);
        setRecentTasks(tasksRes.data.slice(0, 5));
        setCredits(creditsRes.data.balance || 0);
        
        // Fetch unread notifications count
        try {
          const unreadRes = await axios.get(`${API}/comms/notifications/unread-count`, { headers, withCredentials: true });
          setUnreadCount(unreadRes.data.count || 0);
        } catch {
          // Fallback: count pending invites
          try {
            const invitesRes = await axios.get(`${API}/comms/meetings/invites`, { headers, withCredentials: true });
            setUnreadCount(invitesRes.data?.length || 0);
          } catch {
            setUnreadCount(0);
          }
        }
        
        // Fetch supported languages
        try {
          const langRes = await axios.get(`${API}/auth/languages`);
          setLanguages(langRes.data.languages || []);
        } catch {
          // Fallback to common languages
          setLanguages([
            { code: "en", name: "English" },
            { code: "fr", name: "French" },
            { code: "es", name: "Spanish" },
            { code: "pt", name: "Portuguese" },
            { code: "de", name: "German" }
          ]);
        }
        
        // Get user's current language preference
        if (auth.user?.default_language) {
          setUserLanguage(auth.user.default_language);
        }
        
        // Get user's country
        if (auth.user?.country) {
          setUserCountry(auth.user.country);
        } else {
          // Fetch from profile
          try {
            const profileRes = await axios.get(`${API}/auth/me`, { headers, withCredentials: true });
            if (profileRes.data?.country) {
              setUserCountry(profileRes.data.country);
            }
          } catch {}
        }

        // Fetch LLM provider settings
        try {
          const providerRes = await axios.get(`${API}/settings/llm-provider`, { headers, withCredentials: true });
          setLlmProvider(providerRes.data.provider);
          setAvailableProviders(providerRes.data.available || []);
          setProviderModels(providerRes.data.models || {});
        } catch {
          // Non-critical — default provider is used automatically
        }

        // Fetch 2FA status
        try {
          const totpRes = await axios.get(`${API}/auth/2fa/status`, { headers, withCredentials: true });
          setTotpEnabled(totpRes.data.enabled);
        } catch {
          // Non-critical
        }
      } catch (err) {
        console.error("Dashboard fetch error:", err);
      }
    };
    fetchData();
  }, [auth.user]);

  const handleLanguageChange = async (newLang) => {
    setSavingLanguage(true);
    try {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      await axios.put(`${API}/auth/language`, { language: newLang }, { headers, withCredentials: true });
      setUserLanguage(newLang);
      toast.success("Language preference updated! Messages will now be translated to your language.");
    } catch (err) {
      console.error("Failed to update language:", err);
      toast.error("Failed to update language preference");
    }
    setSavingLanguage(false);
  };

  const handleCountryChange = async (newCountry) => {
    setSavingSettings(true);
    try {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      await axios.put(`${API}/auth/profile`, { country: newCountry }, { headers, withCredentials: true });
      setUserCountry(newCountry);
      toast.success("Country updated! Prices will now be shown in your local currency.");
      // Refresh packages with new currency
      fetchCreditPackages();
    } catch (err) {
      console.error("Failed to update country:", err);
      toast.error("Failed to update country");
    }
    setSavingSettings(false);
  };

  const handleSetupTotp = async () => {
    setSettingUpTotp(true);
    setTotpSetupCode("");
    try {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.post(`${API}/auth/2fa/setup`, {}, { headers, withCredentials: true });
      setTotpSecret(res.data.secret);
      setTotpProvisioningUri(res.data.provisioning_uri);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to start 2FA setup");
      setSettingUpTotp(false);
    }
  };

  const handleConfirmTotp = async () => {
    if (!totpSetupCode || totpSetupCode.length < 6) {
      toast.error("Please enter a valid 6-digit code");
      return;
    }
    setConfirmingTotp(true);
    try {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.post(`${API}/auth/2fa/confirm`, { code: totpSetupCode }, { headers, withCredentials: true });
      setTotpEnabled(true);
      setSettingUpTotp(false);
      setTotpSetupCode("");
      toast.success("2FA has been enabled successfully!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to verify code");
    }
    setConfirmingTotp(false);
  };

  const handleDisableTotp = async () => {
    if (!totpDisablePassword) {
      toast.error("Please enter your password to disable 2FA");
      return;
    }
    try {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.post(`${API}/auth/2fa/disable`, { password: totpDisablePassword }, { headers, withCredentials: true });
      setTotpEnabled(false);
      setTotpDisablePassword("");
      toast.success("2FA has been disabled");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to disable 2FA");
    }
  };

  const handleChangePassword = async () => {
    if (newPassword !== confirmPassword) {
      toast.error("New passwords do not match");
      return;
    }
    if (newPassword.length < 6) {
      toast.error("New password must be at least 6 characters");
      return;
    }
    setChangingPassword(true);
    try {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.put(`${API}/auth/profile`, {
        current_password: currentPassword,
        new_password: newPassword,
      }, { headers, withCredentials: true });
      toast.success("Password updated successfully");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to change password");
    }
    setChangingPassword(false);
  };

  const handleProviderChange = async (newProvider) => {
    setSavingProvider(true);
    const prevProvider = llmProvider;
    setLlmProvider(newProvider);
    try {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.put(`${API}/settings/llm-provider`, { provider: newProvider }, { headers, withCredentials: true });
      toast.success(`LLM provider switched to ${newProvider.charAt(0).toUpperCase() + newProvider.slice(1)}`);
    } catch (err) {
      setLlmProvider(prevProvider);
      toast.error(err.response?.data?.detail || "Failed to update LLM provider");
    }
    setSavingProvider(false);
  };

  const fetchCreditPackages = async () => {
    try {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(`${API}/currency/packages`, { headers, withCredentials: true });
      setCreditPackages(res.data.packages || []);
    } catch (err) {
      console.error("Failed to fetch credit packages:", err);
    }
  };

  const handleQuickPurchase = async (packageId) => {
    setPurchasingPackage(packageId);
    try {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const res = await axios.post(`${API}/credits/checkout`, {
        package_id: packageId,
        origin_url: window.location.origin
      }, { headers, withCredentials: true });
      
      if (res.data.url) {
        toast.success("Redirecting to checkout...");
        window.location.href = res.data.url;
      } else {
        toast.error("Failed to create checkout session");
      }
    } catch (err) {
      console.error("Purchase failed:", err);
      toast.error(err.response?.data?.detail || "Purchase failed");
    }
    setPurchasingPackage(null);
  };

  // Fetch credit packages when quick purchase opens
  useEffect(() => {
    if (quickPurchaseOpen && creditPackages.length === 0) {
      fetchCreditPackages();
    }
  }, [quickPurchaseOpen]);

  const handleLogout = async () => {
    await auth.logout();
    navigate("/");
  };

  const hubs = [
    {
      path: "/smart-office",
      icon: Briefcase,
      name: t("nav.smart_office"),
      desc: "Documents, Excel, Translation, Transcription & Files",
      color: "rose",
      stat: stats.documents,
      statLabel: "Documents"
    },
    {
      path: "/vetting",
      icon: Shield,
      name: t("hubs.vetting"),
      desc: "Intelligent vetting & classification operations",
      color: "indigo",
      stat: "-",
      statLabel: t("common.cases")
    },
    {
      path: "/ims",
      icon: Database,
      name: t("hubs.ims"),
      desc: "Multi-source intelligence correlation & reporting",
      color: "emerald",
      stat: "-",
      statLabel: t("common.sources")
    },
    {
      path: "/comms-centre",
      icon: MessageSquare,
      name: t("nav.comms_centre"),
      desc: "Chat, Meetings, Tasks & Collaboration",
      color: "violet",
      stat: stats.tasks,
      statLabel: t("common.items")
    },
    {
      path: "/hr-hub",
      icon: Users,
      name: t("hubs.hr"),
      desc: "Organogram, Performance & Skills Audit",
      color: "emerald",
      stat: stats.members,
      statLabel: t("dashboard.members")
    },
    {
      path: "/monitoring-centre",
      icon: Monitor,
      name: t("hubs.monitoring"),
      desc: "Live drone & camera feeds, alerts",
      color: "red",
      stat: stats.drones,
      statLabel: "Feeds"
    },
    {
      path: "/drones",
      icon: Camera,
      name: "Drones",
      desc: "Live broadcasts, WebRTC streaming & sharing",
      color: "cyan",
      stat: "-",
      statLabel: "Live"
    },
    { 
      path: "/learning", 
      icon: GraduationCap, 
      name: "Learning Centre", 
      desc: "Courses & development resources",
      color: "amber",
      stat: stats.courses,
      statLabel: "Courses"
    },
    { 
      path: "/innovation-centre", 
      icon: Lightbulb, 
      name: "Innovation Centre", 
      desc: "AI-powered idea management pipeline",
      color: "purple",
      stat: "-",
      statLabel: "Ideas"
    },
    {
      path: "/cyber-monitor",
      icon: Shield,
      name: "Cyber Monitor",
      desc: "OSINT investigations, threat intelligence & case management",
      color: "red",
      stat: "-",
      statLabel: "Intel"
    },
    { 
      path: "/knowledge-centre", 
      icon: BookOpen, 
      name: "Knowledge Hub", 
      desc: "SOPs, templates & documentation",
      color: "cyan",
      stat: "-",
      statLabel: "Categories"
    },
    { 
      path: "/alerts", 
      icon: Bell, 
      name: "Alerts Hub", 
      desc: "Breaking news, weather, traffic & more",
      color: "blue",
      stat: "-",
      statLabel: "Alerts"
    },
    { 
      path: "/rewards", 
      icon: Gift, 
      name: "Rewards Centre", 
      desc: "Competitions, offers & AI credits",
      color: "amber",
      stat: "-",
      statLabel: "Rewards"
    },
    {
      path: "/logistics",
      icon: Package,
      name: "Logistics Hub",
      desc: "Asset inventory, fleet management & SCM",
      color: "amber",
      stat: "-",
      statLabel: "Assets"
    },
    {
      path: "/travelling",
      icon: Plane,
      name: "Travelling Admin",
      desc: "Trip requests, expense claims & reconciliation",
      color: "sky",
      stat: "-",
      statLabel: "Trips"
    },
    {
      path: "/request-service",
      icon: LifeBuoy,
      name: "Request Service",
      desc: "IT, logistics, medical, security & office supply",
      color: "teal",
      stat: "-",
      statLabel: "Requests"
    },
    {
      path: "/erp",
      icon: LayoutDashboard,
      name: "ERP Hub",
      desc: "Finance, SCM, production & AI-powered ERP operations",
      color: "indigo",
      stat: "-",
      statLabel: "ERP"
    },
    {
      path: "/smartpay",
      icon: Coins,
      name: "SmartPay Hub",
      desc: "Contractors, HRIS, equipment & resource management",
      color: "teal",
      stat: "-",
      statLabel: "Resources"
    },
    {
      path: "/evet",
      icon: Shield,
      name: "eVET Hub",
      desc: "AI-powered vetting & clearance platform",
      color: "indigo",
      stat: "-",
      statLabel: "Vetting"
    },
    {
      path: "/notice-board",
      icon: Megaphone,
      name: "Notice Board",
      desc: "Announcements, events & org-wide broadcasts",
      color: "violet",
      stat: "-",
      statLabel: "Notices"
    },
    {
      path: "/assembly-hub",
      icon: Cpu,
      name: "Assembly Hub",
      desc: "High-accuracy transcription, WER evaluation & entity extraction",
      color: "emerald",
      stat: "-",
      statLabel: "Transcripts"
    },
    {
      path: "/smart-talk",
      icon: Languages,
      name: "Smart Talk",
      desc: "Real-time multilingual transcription & translation hub",
      color: "cyan",
      stat: "-",
      statLabel: "Languages"
    },
    {
      path: "/smart-office/doc-converter",
      icon: FileText,
      name: "Doc Converter",
      desc: "Convert documents & extract text from images",
      color: "blue",
      stat: "-",
      statLabel: "Formats"
    },
    {
      path: "/smart-office/workdocs",
      icon: FileText,
      name: "WorkDocs",
      desc: "AI-native document creation & editing suite",
      color: "indigo",
      stat: "-",
      statLabel: "Documents"
    },
    {
      path: "/corporate-hub",
      icon: Building2,
      name: "Corporate Hub",
      desc: "Browse organizations, corporates & NGOs",
      color: "violet",
      stat: "-",
      statLabel: "Orgs"
    },
    {
      path: "/people-directory",
      icon: Users,
      name: "People Directory",
      desc: "Connect with members & experts",
      color: "emerald",
      stat: "-",
      statLabel: "People"
    },
    {
      path: "/deals",
      icon: Gift,
      name: "Deals Hub",
      desc: "Daily deals, group buys, bids & exclusive offers",
      color: "amber",
      stat: "-",
      statLabel: "Deals"
    },
    {
      path: "/wish-centre",
      icon: Star,
      name: "Wish Centre",
      desc: "Post wishes, help others & make connections",
      color: "pink",
      stat: "-",
      statLabel: "Wishes"
    },
    {
      path: "/agents-centre",
      icon: Brain,
      name: "Agents Centre",
      desc: "AI profiling & due diligence on entities",
      color: "violet",
      stat: "-",
      statLabel: "Tools"
    },
  ];

  // Permission-based filtering: hub path -> permission key
  const hubPermissions = {
    "/smart-office": "smart_office",
    "/vetting": "vetting",
    "/ims": "ims",
    "/comms-centre": "comms",
    "/hr-hub": "hr",
    "/monitoring-centre": "monitoring",
    "/learning": "learning",
    "/innovation-centre": "innovation",
    "/cyber-monitor": "cyber_monitor",
    "/knowledge-centre": "knowledge",
    "/alerts": "alerts",
    "/rewards": "rewards",
    "/logistics": "logistics",
    "/travelling": "travelling",
    "/request-service": "request_service",
    "/erp": null,
    "/smartpay": "smartpay",
    "/evet": "evet",
    "/notice-board": null,
    "/assembly-hub": null,
    "/smart-talk": null,
    "/smart-office/workdocs": null,
  };

  const userPerms = auth.user?.permissions || {};
  const isAdmin = auth.user?.role === "admin";
  const visibleHubs = hubs.filter(h => {
    if (isAdmin) return true; // Admin sees everything
    const permKey = hubPermissions[h.path];
    return !permKey || userPerms[permKey] !== false;
  });

  const colorMap = {
    blue: { border: "border-t-blue-500", text: "text-blue-400", bg: "bg-blue-500/10" },
    red: { border: "border-t-red-500", text: "text-red-400", bg: "bg-red-500/10" },
    emerald: { border: "border-t-emerald-500", text: "text-emerald-400", bg: "bg-emerald-500/10" },
    amber: { border: "border-t-amber-500", text: "text-amber-400", bg: "bg-amber-500/10" },
    purple: { border: "border-t-purple-500", text: "text-purple-400", bg: "bg-purple-500/10" },
    cyan: { border: "border-t-cyan-500", text: "text-cyan-400", bg: "bg-cyan-500/10" },
    rose: { border: "border-t-rose-500", text: "text-rose-400", bg: "bg-rose-500/10" },
    violet: { border: "border-t-violet-500", text: "text-violet-400", bg: "bg-violet-500/10" },
    indigo: { border: "border-t-indigo-500", text: "text-indigo-400", bg: "bg-indigo-500/10" },
    sky: { border: "border-t-sky-500", text: "text-sky-400", bg: "bg-sky-500/10" },
    teal: { border: "border-t-teal-500", text: "text-teal-400", bg: "bg-teal-500/10" },
    pink: { border: "border-t-pink-500", text: "text-pink-400", bg: "bg-pink-500/10" },
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 overflow-hidden">
      <TopNav />
      {/* Header */}
      <header className="border-b border-app bg-app-card/70 backdrop-blur-xl sticky top-0 z-50 h-10">
        <div className="max-w-7xl mx-auto px-4 flex items-center justify-between h-full">
          <Link to="/" className="flex items-center gap-2">
            <AllBaiseLogo size={26} />
          </Link>

          <div className="flex items-center gap-1 sm:gap-4">
            {/* Language Selector */}
            <LanguageSelector compact />


            
            {/* Notifications/Alerts Icon */}
            <Link to="/comms-centre/notifications">
              <Button 
                variant="ghost"
                size="sm"
                className="relative p-2"
                data-testid="notifications-btn"
              >
                  <Bell className="w-5 h-5 text-slate-300" />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center animate-pulse">
                    {unreadCount > 9 ? "9+" : unreadCount}
                  </span>
                )}
              </Button>
            </Link>
            
            <Link to="/agent" className="hidden sm:inline-flex" data-testid="agent-support-link">
              <Button
                variant="outline"
                className="border-indigo-400/30 text-indigo-300 hover:bg-indigo-500/10 rounded-none"
                data-testid="agent-support-btn"
              >
                <Bot className="w-4 h-4 mr-2" />
                {t("hub.agent")}
              </Button>
            </Link>
            <Link to="/">
              <Button
                variant="outline"
                size="sm"
                className="border-violet-400/30 text-violet-300 hover:bg-violet-500/10 rounded-none"
              >
                {t("common.back")}
              </Button>
            </Link>

            {/* Mobile menu toggle */}
            <div className="sm:hidden">
              <Button variant="ghost" className="p-1" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-testid="mobile-menu-btn">
                <Menu className="w-5 h-5 text-slate-300" />
              </Button>
            </div>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu-btn">
                  <Avatar className="w-7 h-7 sm:w-8 sm:h-8">
                    <AvatarImage src={auth.user?.picture} />
                    <AvatarFallback className="bg-slate-200 text-slate-600 text-xs sm:text-sm">
                      {auth.user?.name?.charAt(0) || "U"}
                    </AvatarFallback>
                  </Avatar>
                  <span className="text-slate-200 text-sm hidden sm:inline">{auth.user?.name}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-slate-900 border-slate-800 rounded-none shadow-lg text-slate-200">
                <DropdownMenuItem className="text-slate-200">
                  {auth.user?.email}
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-slate-800" />
                <DropdownMenuItem 
                  className="cursor-pointer"
                  onClick={() => setSettingsOpen(true)}
                  data-testid="settings-btn"
                >
                  <Settings className="w-4 h-4 mr-2" />
                  {t("nav.settings")}
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-slate-800" />
                <div className="px-2 py-2">
                  <ThemeSwitcher compact />
                </div>
                <DropdownMenuSeparator className="bg-slate-800" />
                {auth.user?.role === "admin" && (
                  <>
                    <DropdownMenuItem className="cursor-pointer" onClick={() => navigate("/admin")}>
                      <Shield className="w-4 h-4 mr-2 text-indigo-400" />
                      <span className="text-indigo-400">{t("hub.admin")}</span>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator className="bg-slate-800" />
                  </>
                )}
                <DropdownMenuItem
                  className="text-red-600 cursor-pointer"
                  onClick={handleLogout}
                  data-testid="logout-btn"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  {t("nav.logout")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* Mobile Navigation Overlay */}
      {mobileMenuOpen && (
        <div className="sm:hidden fixed inset-0 z-40" data-testid="mobile-nav-overlay">
          <div className="absolute inset-0 bg-slate-950/95 backdrop-blur-lg" onClick={() => setMobileMenuOpen(false)} />
          <div className="relative z-10 flex flex-col items-center justify-start pt-20 px-6 gap-2">
            <Link to="/agent" className="w-full" onClick={() => setMobileMenuOpen(false)}>
              <Button variant="outline" className="w-full border-indigo-400/30 text-indigo-300 hover:bg-indigo-500/10 py-6 text-base">
                <Bot className="w-5 h-5 mr-3" />
                Agent Support
              </Button>
            </Link>
            <Link to="/rewards" className="w-full" onClick={() => setMobileMenuOpen(false)}>
              <Button variant="outline" className="w-full border-emerald-400/30 text-emerald-300 hover:bg-emerald-500/10 py-6 text-base">
                <Gift className="w-5 h-5 mr-3" />
                Rewards Centre
              </Button>
            </Link>
            <button
              onClick={() => { setMobileMenuOpen(false); setSettingsOpen(true); }}
              className="w-full py-4 text-center text-slate-300 border border-white/10 hover:bg-white/5 text-base transition-colors"
            >
              <Settings className="w-5 h-5 inline mr-3" />
              {t("nav.settings")}
            </button>
            {auth.user?.role === "admin" && (
              <Link to="/admin" className="w-full" onClick={() => setMobileMenuOpen(false)}>
                <Button variant="outline" className="w-full border-indigo-400/30 text-indigo-300 hover:bg-indigo-500/10 py-6 text-base">
                  <Shield className="w-5 h-5 mr-3" />
                  {t("hub.admin")}
                </Button>
              </Link>
            )}
            <button
              onClick={() => { logout(); setMobileMenuOpen(false); }}
              className="w-full py-4 text-center text-red-400 border border-red-400/20 hover:bg-red-500/10 text-base transition-colors mt-4"
            >
              <LogOut className="w-5 h-5 inline mr-3" />
              {t("nav.logout")}
            </button>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Welcome Section */}
        <div className="mb-6 sm:mb-8">
          <h1 className="font-['Barlow_Condensed'] text-3xl sm:text-4xl font-bold tracking-tight text-white mb-2">
            Welcome to the AllBaise
          </h1>
          <p className="text-slate-400 text-sm sm:text-base">Office Bespoke AI Scalable Engine</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 mb-8">
          <div className="md:col-span-7 bg-slate-900/80 border border-white/10 p-6 relative overflow-hidden">
            <div className="absolute inset-0 bg-[linear-gradient(rgba(59,130,246,0.06)_1px,transparent_1px),linear-gradient(90deg,rgba(59,130,246,0.06)_1px,transparent_1px)] bg-[size:4rem_4rem] opacity-50" />
            <div className="relative flex flex-col gap-6">
              <div>
                <p className="text-xs uppercase tracking-[0.3em] text-slate-500 mb-2">Mission Overview</p>
                <h2 className="font-['Barlow_Condensed'] text-3xl font-semibold text-white">Nine hubs, one command layer.</h2>
                <p className="text-slate-400 mt-2 max-w-2xl">
                  Move between operations, communications, human resources, learning, intelligence, and rewards without leaving the platform.
                </p>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                  { label: "Documents", value: stats.documents, tone: "blue" },
                  { label: "Members", value: stats.members, tone: "emerald" },
                  { label: "Tasks", value: stats.tasks, tone: "violet" },
                   { label: "AI", value: credits, tone: "amber" },
                ].map((item) => (
                  <div key={item.label} className="bg-white/5 border border-white/10 p-4">
                    <p className="text-xs uppercase tracking-[0.2em] text-slate-500">{item.label}</p>
                    <p className={`mt-2 text-2xl font-semibold ${colorMap[item.tone].text}`}>{item.value}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="md:col-span-5 grid gap-4">
            <div className="bg-slate-900/80 border border-white/10 p-5">
              <p className="text-xs uppercase tracking-[0.3em] text-slate-500 mb-2">Notifications</p>
              <p className="text-3xl font-semibold text-white">{unreadCount}</p>
              <p className="text-slate-400 text-sm mt-2">Unread alerts and meeting invites are waiting in Comms Centre.</p>
            </div>
            <div className="bg-slate-900/80 border border-white/10 p-5">
              <p className="text-xs uppercase tracking-[0.3em] text-slate-500 mb-2">Quick Access</p>
              <p className="text-slate-200 text-sm mb-4">Jump straight to the modules users touch most.</p>
              <div className="flex flex-wrap gap-2">
                <Link to="/agent"><Button variant="outline" className="border-slate-700 text-slate-200 hover:bg-white/5 rounded-none">Agent Support</Button></Link>
                <Link to="/rewards"><Button variant="outline" className="border-slate-700 text-slate-200 hover:bg-white/5 rounded-none">Rewards Centre</Button></Link>
                <Link to="/notice-board"><Button variant="outline" className="border-slate-700 text-slate-200 hover:bg-white/5 rounded-none">Notice Board</Button></Link>
              </div>
            </div>
          </div>
        </div>

        {/* Bento Grid - Hubs */}
        <div className="flex gap-4">
          <div className="flex-1 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-4 mb-8">
            {visibleHubs.map((hub, idx) => {
            const Icon = hub.icon;
            const colors = colorMap[hub.color];
            
            return (
              <Link 
                key={hub.path}
                to={hub.path}
                className={`block bg-slate-900/80 border border-white/10 border-t-2 ${colors.border} p-6 card-hover group fade-in stagger-${idx + 1}`}
                style={{ opacity: 0 }}
                data-testid={`hub-card-${hub.path.slice(1)}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className={`w-12 h-12 ${colors.bg} flex items-center justify-center`}>
                    <Icon className={`w-6 h-6 ${colors.text}`} />
                  </div>
                  <ChevronRight className="w-5 h-5 text-slate-500 group-hover:text-slate-200 transition-colors" />
                </div>
                
                <h3 className="font-['Barlow_Condensed'] text-xl font-semibold text-white mb-1">
                  {hub.name}
                </h3>
                <p className="text-sm text-slate-400 mb-4">{hub.desc}</p>
                
                <div className="flex items-center gap-2">
                  <span className={`font-['JetBrains_Mono'] text-2xl font-medium ${colors.text}`}>
                    {hub.stat}
                  </span>
                  <span className="text-xs text-slate-500 uppercase tracking-wider">
                    {hub.statLabel}
                  </span>
                </div>
              </Link>
            );
          })}
        </div>
          <div className="hidden lg:block w-40 shrink-0 pt-3">
            <HubSwitcher />
          </div>
        </div>

        {/* Bottom Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Agent Support Card */}
          <Link
            to="/agent"
            className="block bg-slate-900/80 border border-white/10 p-6 card-hover group"
            data-testid="agent-support-card"
          >
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 bg-indigo-500/10 flex items-center justify-center">
                <Bot className="w-7 h-7 text-indigo-300" />
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-['Barlow_Condensed'] text-xl font-semibold text-white">
                    Agent Support
                  </h3>
                  <ChevronRight className="w-5 h-5 text-indigo-300 group-hover:translate-x-1 transition-transform" />
                </div>
                <p className="text-sm text-slate-400">
                  Ask anything about your documents, HR policies, courses, or organizational knowledge.
                </p>
              </div>
            </div>
          </Link>

          {/* Recent Tasks */}
          <div className="bg-slate-900/80 border border-white/10 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-['Barlow_Condensed'] text-xl font-semibold text-white">
                Recent Tasks
              </h3>
              <Link to="/tasks" className="text-sm text-purple-300 hover:text-purple-200">
                View all
              </Link>
            </div>
            
            {recentTasks.length > 0 ? (
              <div className="space-y-2">
                {recentTasks.map((task) => (
                  <div 
                    key={task.id}
                    className="flex items-center justify-between py-2 border-b border-white/5 last:border-0"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-2 h-2 rounded-full ${
                        task.priority === 'high' ? 'bg-red-500' :
                        task.priority === 'medium' ? 'bg-amber-500' : 'bg-slate-400'
                      }`} />
                      <span className="text-sm text-slate-200">{task.title}</span>
                    </div>
                    <span className="text-xs text-slate-500 font-['JetBrains_Mono']">
                      {task.due_date || "-"}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-400 text-sm">No tasks yet. Create your first task!</p>
            )}
          </div>
        </div>
      </main>

      {/* Settings Dialog */}
      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent className="mobile-fullscreen-dialog sm:max-w-md bg-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-slate-800">
              <Settings className="w-5 h-5" />
              {t("nav.settings")}
            </DialogTitle>
            <DialogDescription className="text-slate-600">
              Customize your preferences
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Language Preference */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Globe className="w-4 h-4 text-violet-600" />
                <h3 className="font-medium text-slate-800">Translation Language</h3>
              </div>
              <p className="text-sm text-slate-600">
                Messages from other users will be automatically translated to your preferred language.
              </p>
              <Select 
                value={userLanguage} 
                onValueChange={handleLanguageChange}
                disabled={savingLanguage}
              >
                <SelectTrigger 
                  className="w-full bg-white border-slate-200"
                  data-testid="language-select"
                >
                  <SelectValue placeholder="Select language" />
                </SelectTrigger>
                <SelectContent className="bg-white border-slate-200 max-h-64">
                  {languages.map((lang) => (
                    <SelectItem 
                      key={lang.code} 
                      value={lang.code}
                      data-testid={`lang-option-${lang.code}`}
                    >
                      {lang.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {savingLanguage && (
                <p className="text-xs text-violet-600">Saving...</p>
              )}
            </div>

            {/* Current Language Display */}
            <div className="p-3 bg-app rounded-lg border border-slate-100">
              <p className="text-sm text-slate-600">
                <span className="font-medium">Current:</span>{" "}
                {languages.find(l => l.code === userLanguage)?.name || "English"}
              </p>
              <p className="text-xs text-slate-500 mt-1">
                Chat messages will be translated to this language
              </p>
            </div>

            {/* LLM Provider Preference */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Cpu className="w-4 h-4 text-blue-600" />
                <h3 className="font-medium text-slate-800">LLM Provider</h3>
              </div>
              <p className="text-sm text-slate-600">
                Select which AI provider handles chat and tool calls. DeepSeek is more cost-effective at scale.
              </p>
              <Select
                value={llmProvider}
                onValueChange={handleProviderChange}
                disabled={savingProvider || availableProviders.length === 0}
              >
                <SelectTrigger className="w-full bg-white border-slate-200">
                  <SelectValue placeholder="Select provider" />
                </SelectTrigger>
                <SelectContent className="bg-white border-slate-200">
                  {availableProviders.map((p) => (
                    <SelectItem key={p} value={p}>
                      <span className="capitalize">{p}</span>
                      {providerModels[p] && (
                        <span className="ml-2 text-xs text-slate-400">
                          ({providerModels[p]})
                        </span>
                      )}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {savingProvider && (
                <p className="text-xs text-blue-600">Saving...</p>
              )}
              {availableProviders.length === 0 && (
                <p className="text-xs text-amber-600">No providers configured. Contact your administrator.</p>
              )}
            </div>

            {/* Change Password */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Shield className="w-4 h-4 text-slate-600" />
                <h3 className="font-medium text-slate-800">Change Password</h3>
              </div>
              <div className="space-y-2">
                <Input
                  type="password"
                  placeholder="Current password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className="bg-white border-slate-200"
                />
                <Input
                  type="password"
                  placeholder="New password (min 6 characters)"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="bg-white border-slate-200"
                />
                <Input
                  type="password"
                  placeholder="Confirm new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="bg-white border-slate-200"
                />
                <Button
                  size="sm"
                  onClick={handleChangePassword}
                  disabled={!currentPassword || !newPassword || !confirmPassword || changingPassword}
                  className="bg-slate-700 hover:bg-slate-600 text-white"
                >
                  {changingPassword ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                  Update Password
                </Button>
              </div>
            </div>

            {/* Two-Factor Authentication */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Shield className="w-4 h-4 text-red-600" />
                <h3 className="font-medium text-slate-800">Two-Factor Authentication</h3>
              </div>
              <p className="text-sm text-slate-600">
                Add an extra layer of security by requiring a verification code from Google Authenticator or similar app when logging in.
              </p>
              {totpEnabled ? (
                <div className="space-y-3">
                  <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-100">
                    <p className="text-sm text-emerald-700 font-medium flex items-center gap-2">
                      <span className="w-2 h-2 bg-emerald-500 rounded-full" />
                      2FA is currently enabled
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Input
                      type="password"
                      placeholder="Enter your password to disable 2FA"
                      value={totpDisablePassword}
                      onChange={(e) => setTotpDisablePassword(e.target.value)}
                      className="bg-white border-slate-200"
                    />
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={handleDisableTotp}
                      disabled={!totpDisablePassword}
                    >
                      Disable 2FA
                    </Button>
                  </div>
                </div>
              ) : settingUpTotp ? (
                <div className="space-y-3 p-3 bg-app rounded-lg border border-slate-100">
                  {totpProvisioningUri && (
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-slate-700">Scan this QR code with Google Authenticator:</p>
                      <div className="flex justify-center p-2 bg-white rounded-lg">
                        <img
                          src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(totpProvisioningUri)}`}
                          alt="TOTP QR Code"
                          className="w-48 h-48"
                        />
                      </div>
                      <details className="text-xs text-slate-500">
                        <summary className="cursor-pointer hover:text-slate-700">Or enter the secret manually</summary>
                        <p className="mt-1 font-mono bg-slate-100 p-1 rounded select-all">{totpSecret}</p>
                      </details>
                    </div>
                  )}
                  <div className="space-y-2">
                    <p className="text-sm text-slate-600">Enter the 6-digit code from the app to verify:</p>
                    <Input
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      placeholder="000000"
                      value={totpSetupCode}
                      onChange={(e) => setTotpSetupCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                      className="bg-white border-slate-200 text-center text-lg tracking-widest"
                    />
                    <div className="flex gap-2">
                      <Button
                        variant="default"
                        onClick={handleConfirmTotp}
                        disabled={confirmingTotp || totpSetupCode.length < 6}
                      >
                        {confirmingTotp ? "Verifying..." : "Verify & Enable"}
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => setSettingUpTotp(false)}
                        disabled={confirmingTotp}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                </div>
              ) : (
                <Button
                  variant="outline"
                  onClick={handleSetupTotp}
                  className="border-slate-200"
                >
                  Enable 2FA
                </Button>
              )}
            </div>

            {/* Country Preference */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-emerald-600" />
                <h3 className="font-medium text-slate-800">Country</h3>
              </div>
              <p className="text-sm text-slate-600">
                Your country is used to show prices in your local currency and personalize alerts.
              </p>
              <Select 
                value={userCountry} 
                onValueChange={handleCountryChange}
                disabled={savingSettings}
              >
                <SelectTrigger 
                  className="w-full bg-white border-slate-200"
                  data-testid="country-select"
                >
                  <SelectValue placeholder="Select country" />
                </SelectTrigger>
                <SelectContent className="bg-white border-slate-200 max-h-64">
                  {COUNTRIES.map((country) => (
                    <SelectItem 
                      key={country.code} 
                      value={country.code}
                      data-testid={`country-option-${country.code}`}
                    >
                      {country.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {savingSettings && (
                <p className="text-xs text-emerald-600">Saving...</p>
              )}
            </div>

            {/* Current Country Display */}
            <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-100">
              <p className="text-sm text-slate-600">
                <span className="font-medium">Current:</span>{" "}
                {COUNTRIES.find(c => c.code === userCountry)?.name || "Not set"}
              </p>
              <p className="text-xs text-slate-500 mt-1">
                Prices in Rewards Centre will be shown in your local currency
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
